import type { EditorContext } from '@pyranger/types';
import { debug } from '@pyranger/utils';

/**
 * Write data to the xterm.js terminal instance with formatting applied
 * via the TerminalFormatter (traceback colorization, prompt styling, etc.).
 *
 * Uses requestAnimationFrame to batch rapid writes — at most one term.write()
 * per frame (~60fps). This prevents the UI from locking up when the device
 * sends data faster than the screen can repaint.
 */
export const writeToTerminal = ({
    context,
    message,
}: {
    context: EditorContext;
    message: string;
}) => {
    const { ref } = context;

    // Accumulate into the buffer
    ref.current.terminal.messageBuffer += message;

    // Schedule a flush on the next animation frame (if not already scheduled)
    if (!ref.current.terminal.flushScheduled) {
        ref.current.terminal.flushScheduled = true;
        requestAnimationFrame(() => {
            ref.current.terminal.flushScheduled = false;
            flushTerminalBuffer(context);
        });
    }
};

/**
 * Flush the terminal buffer — format and write to xterm.
 */
function flushTerminalBuffer(context: EditorContext): void {
    const { ref } = context;
    const { messageBuffer, instance: term } = ref.current.terminal;
    const { terminalFormatter } = ref.current.device;

    if (!messageBuffer || !term) return;

    const formatted = terminalFormatter.format(messageBuffer);
    term.write(formatted);
    ref.current.terminal.messageBuffer = '';
}

export const resetTerminal = ({ context }: { context: EditorContext }) => {
    const { ref } = context;

    const {
        current: {
            terminal: { instance: term },
            device: { terminalFormatter },
        },
    } = ref;

    if (term) {
        term.reset();
        term.clear();
    }

    ref.current.terminal.isTextSelected = false;
    ref.current.terminal.messageBuffer = '';
    ref.current.terminal.flushScheduled = false;

    terminalFormatter.reset();
};

export const handleCopy = ({ context }: { context: EditorContext }) => {
    const { ref } = context;

    const {
        current: {
            terminal: { instance: term },
        },
    } = ref;

    if (term && term.hasSelection()) {
        const selectedText = term.getSelection();
        navigator.clipboard
            .writeText(selectedText)
            .then(() => {
                debug.editor('Text copied to clipboard');
            })
            .catch((err) => {
                debug.editor('Failed to copy text: ', err);
            });
    }
};
